import { useEffect, useState } from "react";
import moment from "moment";
import { Grid, MenuItem } from "@material-ui/core";
import { GeneralModal } from ".";
import {
  ButtonWithLoadingIndicator,
  CancelButton,
  KeyboardDatePickerInput,
  PrimaryTextButton,
  TextFieldInput,
} from "../components-atoms";
import { ITheme } from "../constants/theme";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { makeStyles } from "@material-ui/styles";
import DateFnsUtils from "@date-io/date-fns";
import { useDispatch, useSelector } from "react-redux";
import { getDiscountType, getUseType } from "../utils";
import { deactivatePromotionCode, editPromotionCode } from "../redux/actions/promotionCodes";
import { useTranslation } from 'react-i18next';

interface IPromotionCode {
  Id: string;
  Created: Date;
  Expires: Date;
  StartDate: Date;
  MinimumSpend: number | null;
  Code: string;
  Amount: number;
  Percentage: number;
  PromoCodeType: string;
  CreatedFor?: string;
  Active: boolean;
}
interface IProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onDeactivate?: () => void;
}

export const EditPromotionCodeModal = ({
  visible,
  onClose,
  onSubmit,
  onDeactivate
}: IProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const [dateError, setDateError] = useState(false);
  const [promotionCode, loading]: [IPromotionCode, boolean] = useSelector(
    (state: any) => [
      state.PromotionCodesSymbolsReducer.current,
      state.PromotionCodesSymbolsReducer.loading,
    ]
  );
  const [expires, setExpires] = useState<Date>(new Date(promotionCode.Expires));
  useEffect(() => {
    setExpires(new Date(promotionCode.Expires));
  }, [promotionCode]);
  const onFormSubmit = () => {
    // Format date as ISO date string (YYYY-MM-DD) without time/timezone
    const formatDateOnly = (date: Date) => {
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    dispatch(
      editPromotionCode(
        promotionCode.Id,
        {
          Expires: formatDateOnly(expires),
        },
        () => {
          onClose();
          onSubmit();
        }
      )
    );
  };

  const deactivate = () => {
    dispatch(
      deactivatePromotionCode(
        promotionCode.Id,
        () => {
          onDeactivate?.();
          onClose();
        }
      )
    );
  }

  const isPercent: boolean =
    getDiscountType(promotionCode.PromoCodeType)?.value === "Percent";
  const compareDate = (
    d1: Date,
    d2: Date
  ): { [key: string]: () => boolean } => ({
    greater: () => {
      const diff = moment(d1).diff(moment(d2), "days");
      return diff >= 0;
    },
    less: () => {
      const diff = moment(d1).diff(moment(d2), "days");
      return diff < 0;
    },
    equals: () => moment(d1).isSame(moment(d2), 'day'),
  });
  return (
    promotionCode && (
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <GeneralModal
          title="Edit Promotion Code"
          visible={visible}
          onClose={() => {
            onClose();
          }}
        >
          <form>
            <GeneralModal.ContentArea>
              <fieldset className={classes.fieldset}>
                <Grid container spacing={3}>
                  <Grid container item xs={8}>
                    <TextFieldInput
                      disabled
                      label="Promotion Code"
                      value={promotionCode.Code}
                    />
                  </Grid>
                  <Grid
                    style={{ padding: 0 }}
                    container
                    item
                    xs={4}
                    justifyContent="flex-start"
                    alignItems="center"
                  >
                    <PrimaryTextButton
                      data-automation="generate-code-button"
                      disabled
                      title="Generate Code"
                    />
                  </Grid>
                </Grid>

                <TextFieldInput
                  disabled
                  select
                  label="Discount type"
                  value={getDiscountType(promotionCode.PromoCodeType)?.value}
                >
                  <MenuItem value={"Percent"}>Percentage</MenuItem>
                  <MenuItem value={"Value"}>Fixed Amount</MenuItem>
                </TextFieldInput>
                <TextFieldInput
                  disabled
                  label={`Discount ${isPercent ? "Percentage" : "Amount"}`}
                  type="number"
                  InputProps={
                    isPercent
                      ? {
                        endAdornment: (
                          <span className={classes.suffix}>%</span>
                        ),
                      }
                      : {
                        startAdornment: (
<span className={classes.prefix}>{t('currencySymbol')}</span>
                        ),
                      }
                  }
                  value={
                    promotionCode.PromoCodeType
                      ? promotionCode.PromoCodeType.includes("Percent")
                        ? promotionCode.Percentage
                        : promotionCode.Amount / 100
                      : 0
                  }
                />
                <TextFieldInput
                  disabled
                  label="Min. Spend Required (Opt.)"
                  type="number"
                  value={
                    promotionCode.MinimumSpend
                      ? promotionCode.MinimumSpend / 100
                      : null
                  }
                />
                <TextFieldInput
                  disabled
                  select
                  label="Use Type"
                  value={getUseType(promotionCode.PromoCodeType)?.value}
                >
                  <MenuItem value={"SingleUse"}>Single Use</MenuItem>
                  <MenuItem value={"MultiUse"}>Multiple Use</MenuItem>
                  <MenuItem value={"OncePerCustomer"}>
                    Once Per Customer
                  </MenuItem>
                </TextFieldInput>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <KeyboardDatePickerInput
                      disabled
                      label="Start Date"
                      defaultValue={null}
                      value={promotionCode.StartDate}
                      onChange={() => { }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <KeyboardDatePickerInput
                      placeholder="End Date"
                      label="End Date"
                      name="Expires"
                      minDate={new Date()}
                      minDateMessage=""
                      onError={(e) => {
                        if (e) {
                          setDateError(true);
                        } else {
                          setDateError(false);
                        }
                      }}
                      onChange={(date) => {
                        date &&
                          setExpires(new Date(date?.toDateString() || ""));
                      }}
                      value={expires}
                    />
                  </Grid>
                </Grid>
              </fieldset>
            </GeneralModal.ContentArea>
            <GeneralModal.ButtonArea style={{ justifyContent: 'space-between' }}>
              <Grid>
                <ButtonWithLoadingIndicator
                  onClick={deactivate}
                  disabled={
                    loading ||
                    dateError ||
                    compareDate(promotionCode.Expires, new Date()).less() ||
                    compareDate(promotionCode.Expires, new Date()).equals()
                  }
                  title="Deactivate"
                  type="submit"
                  loading={loading}
                />
              </Grid>
              <Grid>
                <CancelButton
                  onClick={() => {
                    onClose();
                    setExpires(new Date(promotionCode.Expires));
                  }}
                />
                <ButtonWithLoadingIndicator
                  onClick={onFormSubmit}
                  disabled={
                    loading ||
                    dateError ||
                    compareDate(expires, new Date()).less() ||
                    compareDate(expires, new Date()).equals() ||
                    compareDate(expires, new Date(promotionCode.Expires)).equals()
                  }
                  title="Submit"
                  type="submit"
                  loading={loading}
                />
              </Grid>
            </GeneralModal.ButtonArea>
          </form>
        </GeneralModal>
      </MuiPickersUtilsProvider>
    )
  );
};

const useStyles = makeStyles((theme: ITheme) => ({
  fieldset: {
    margin: "16px 0 0",
    padding: 0,
    border: 0,
  },
  prefix: {
    color: theme.palette.action.disabled,
    marginRight: 5,
    marginTop: 2,
  },
  suffix: {
    color: theme.palette.action.disabled,
  },
}));
